/**
 * The client for dwlf-identity (https://id.dwlf.co.uk): sign-in, refresh and sign-out for one
 * product. Holds no state; the session manager (./sessionManager.js) keeps the tokens. Ported from
 * dwlf-metals-app's src/api/identity.ts.
 *
 * Identity's rule for a refused or unreachable call (its charter: "a 429 is wait, never signed
 * out"): a 429, or a network error (the gateway's own throttling response carries no CORS headers,
 * so the browser sees no status), means wait and retry the same call. Only a real answer from
 * identity ends a session.
 */
import { AccountChoiceError, IdentityError, IdentityUnavailableError, RefreshInProgressError } from './errors';

/** The longest single wait, whatever Retry-After asks for: the person is watching a spinner. */
const MAX_WAIT_MS = 30_000;
const DEFAULT_ATTEMPTS = 4;
const DEFAULT_TIMEOUT_MS = 10_000;

const realSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** How long to wait before attempt `n + 1` (n from 1): Retry-After when given, else backoff with jitter. */
export function waitBefore(n, retryAfter, random) {
  const seconds = Number(retryAfter);
  if (retryAfter !== null && retryAfter.trim() !== '' && Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1000, MAX_WAIT_MS);
  }
  return Math.min(1000 * 2 ** (n - 1) + Math.floor(random() * 1000), MAX_WAIT_MS);
}

const errorWith = (body, status) => new IdentityError(body?.error ?? `Sign-in service error (${status})`, status);

const readJson = (res) => res.json().catch(() => null);

/**
 * `once`: the call changes state in a way a repeat would not undo (a refresh rotates the token,
 * sign-in starts a session). An attempt that timed out may have been processed, so these are never
 * re-sent after a timeout; the caller gets IdentityUnavailableError (not a sign-out). Sign-out is
 * safe to repeat.
 */
function transport(baseUrl, options) {
  const root = `${baseUrl.replace(/\/$/, '')}/v1`;
  const attempts = options.attempts ?? DEFAULT_ATTEMPTS;
  const sleep = options.sleep ?? realSleep;
  const random = options.random ?? Math.random;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetch ?? ((...args) => fetch(...args));

  /**
   * One attempt, aborted after timeoutMs. `unreachable`: no answer, and identity's 429 rule says the
   * request was not processed. `timeout`: we stopped waiting, and identity may have processed it.
   */
  async function attempt(url, init) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetchImpl(url, { ...init, signal: controller.signal });
    } catch {
      return controller.signal.aborted ? 'timeout' : 'unreachable';
    } finally {
      clearTimeout(timer);
    }
  }

  return async function call(path, { method, body, once }) {
    const headers = { Accept: 'application/json' };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    for (let n = 1; ; n++) {
      const res = await attempt(`${root}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      const answered = typeof res === 'object';
      if (answered && res.status !== 429) {
        return res;
      }
      if (n >= attempts || (res === 'timeout' && once)) {
        throw new IdentityUnavailableError();
      }
      await sleep(waitBefore(n, answered ? res.headers.get('Retry-After') : null, random));
    }
  };
}

/**
 * @param {string} baseUrl identity's origin, e.g. https://id.dwlf.co.uk
 * @param {string} product the identity product id (this app's token audience)
 */
export function createIdentityClient(baseUrl, product, options = {}) {
  const call = transport(baseUrl, options);

  return {
    /** A Google ID token → an identity session; AccountChoiceError when several accounts hold this product. */
    async signIn(idToken, account) {
      const res = await call('/sign-in', { method: 'POST', body: { idToken, product, ...(account ? { account } : {}) }, once: true });
      if (res.status === 409) {
        // Read once: a body can be consumed only once, and identity's own message must survive.
        const body = await readJson(res);
        throw Array.isArray(body?.accounts) ? new AccountChoiceError(body.accounts) : errorWith(body, 409);
      }
      if (!res.ok) {
        throw errorWith(await readJson(res), res.status);
      }
      return res.json();
    },
    async refresh(refreshToken) {
      const res = await call('/refresh', { method: 'POST', body: { refreshToken }, once: true });
      if (res.status === 409) {
        const body = await readJson(res);
        throw body?.code === 'refresh_in_progress' ? new RefreshInProgressError() : errorWith(body, 409);
      }
      if (!res.ok) {
        throw errorWith(await readJson(res), res.status);
      }
      return res.json();
    },
    async signOut(refreshToken) {
      const res = await call('/sign-out', { method: 'POST', body: { refreshToken } });
      if (!res.ok) {
        throw errorWith(await readJson(res), res.status);
      }
    },
  };
}
